import type { DropdownMenuContentProps, DropdownMenuProps } from '@radix-ui/react-dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { isNotNil, isNotUndefined } from '@wistia/type-guards';
import type { JSX, ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { styled, css, keyframes } from 'styled-components';
import { mq } from '../../helpers/mq';
import type { ButtonProps } from '../Button';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { MenuContext } from './MenuContext';

type ControlledMenuProps = {
  /**
   * Is the menu open.
   * Must be used with `onOpenChange`.
   */
  isOpen: boolean;

  /** When an attempt to toggle the menu is triggered.
   *  Must be used with `isOpen`.
   */
  onOpenChange: (isOpen: boolean) => void;
};

type UncontrolledMenuProps = {
  isOpen?: never;
  onOpenChange?: never;
};

type MenuControlProps = ControlledMenuProps | UncontrolledMenuProps;

export type MenuProps = MenuControlProps & {
  /**
   * The position of the Menu
   */
  align?: 'center' | 'end' | 'start';

  /**
   * The content of the menu.
   * Should be made up of `MenuItem`, `SubMenu`, `Divider`, or other subcomponents listed
   */
  children: ReactNode;

  /**
   * If the menu is compact
   */
  compact?: boolean;

  /**
   * If disabled, the trigger will appear disabled and the menu will not open
   */
  disabled?: boolean;

  /**
   * The label for the trigger element, this is ignored if `trigger` is defined
   */
  label?: string;

  /**
   * Callback for when the user clicks or focuses outside of the menu
   */
  onInteractOutside?: DropdownMenuContentProps['onInteractOutside'];

  /**
   * The side the menu appears on, relative to the trigger
   */
  side?: 'bottom' | 'top';

  /**
   * Replace the default trigger with a custom button or other element
   */
  trigger?: ReactNode;

  /**
   * Props to pass to the trigger component, the default trigger is Button
   */
  triggerProps?: Partial<ButtonProps>;
};

const open = keyframes`
  from {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const close = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
`;

const menuItemCss = css`
  --menu-label-description-gap: var(--wui-space-01);
  --menu-item-inner-gap: var(--wui-space-03);
  --menu-item-left-icon-size: 24px;
  --menu-item-right-icon-size: 16px;
  --menu-item-padding: var(--wui-space-02);
  --menu-label-font-size: var(--wui-typography-label-3-size);
  --menu-label-font-weight: var(--wui-typography-label-3-weight);
  --menu-label-line-height: var(--wui-typography-label-3-line-height);
  --menu-divider-margin: var(--wui-space-02);
`;

const compactMenuItemCss = css`
  --menu-label-description-gap: 0;
  --menu-item-inner-gap: var(--wui-space-02);
  --menu-item-left-icon-size: 16px;
  --menu-item-right-icon-size: 12px;
  --menu-item-padding: 6px;
  --menu-label-font-size: var(--wui-typography-label-4-size);
  --menu-label-font-weight: var(--wui-typography-label-4-weight);
  --menu-label-line-height: var(--wui-typography-label-4-line-height);
  --menu-divider-margin: var(--wui-space-01);
`;

export const menuContentCss = css<{ $compact: boolean }>`
  --menu-font-family: var(--wui-typography-family-default);
  --menu-bg: var(--wui-color-bg-surface);
  --menu-shadow: var(--wui-elevation-01);
  --menu-border-radius: var(--wui-border-radius-02);
  --menu-padding: var(--wui-space-02);
  --menu-min-width: 120px;
  --menu-max-width: 284px;
  --menu-animation-duration: var(--wui-motion-duration-03);

  ${({ $compact }) => ($compact ? compactMenuItemCss : menuItemCss)};

  animation: var(--menu-animation-duration) var(--wui-motion-ease-out) ${open};
  font-family: var(--menu-font-family);
  background: var(--menu-bg);
  border-radius: var(--menu-border-radius);
  box-shadow: var(--menu-shadow); /* TODO - Need a box-shadow token */
  max-height: var(--radix-dropdown-menu-content-available-height);
  min-width: var(--menu-min-width);
  max-width: var(--menu-max-width);
  overflow: auto;
  padding: var(--menu-padding);
  transform-origin: var(--radix-dropdown-menu-content-transform-origin);
  z-index: var(--wui-zindex-menu);
  border: 1px solid var(--wui-color-border);

  &[data-state='closed'] {
    animation: var(--menu-animation-duration) var(--wui-motion-ease-in) ${close}; /* TODO - need easing tokens */
  }

  ${mq.smAndUp} {
    bottom: 0;
    padding: var(--menu-padding);
    right: 0;
    top: 0;
    transform: initial;
  }

  [role='separator'] {
    margin: var(--menu-divider-margin) 0;
  }
`;

const MenuContent = styled(DropdownMenuContent)<{ $compact: boolean }>`
  ${menuContentCss}
  min-width: var(--radix-dropdown-menu-trigger-width);
`;

/**
 * Displays a menu to the users with a set of actions.
 */
export const Menu = forwardRef<HTMLButtonElement, MenuProps>(
  (
    {
      align = 'start',
      children,
      disabled = false,
      compact = false,
      trigger,
      label,
      isOpen,
      side = 'bottom',
      triggerProps = {} as Partial<ButtonProps>,
      onOpenChange,
      ...props
    }: MenuProps,
    ref,
  ): JSX.Element => {
    const contextValue = useMemo(() => ({ compact }), [compact]);

    let controlProps = {
      ...(isNotNil(onOpenChange) && isNotNil(isOpen) ? { open: isOpen, onOpenChange } : {}),
    };

    if (disabled) {
      controlProps = {
        open: false,
        onOpenChange: () => null,
      } as Partial<DropdownMenuProps>;
    }

    return (
      <MenuContext.Provider value={contextValue}>
        <DropdownMenu
          modal={false}
          {...controlProps}
        >
          <DropdownMenuTrigger asChild={true}>
            {isNotUndefined(trigger) ? (
              trigger
            ) : (
              <Button
                ref={ref}
                aria-expanded={isOpen}
                disabled={disabled}
                forceState={isOpen ? 'active' : undefined}
                rightIcon={<Icon type="caret-down" />}
                {...triggerProps}
              >
                {label}
              </Button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <MenuContent
              {...props}
              $compact={compact}
              align={align}
              collisionPadding={8}
              onFocusOutside={(event) => {
                event.preventDefault();
              }}
              side={side}
              sideOffset={6}
            >
              {children}
            </MenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
      </MenuContext.Provider>
    );
  },
);

Menu.displayName = 'Menu';

Menu.displayName = 'Menu';
